import { parseIsoDate } from '@/modules/dashboards/dashboardRanges';
import {
  NOTIFICATION_DELIVERY_CHANNELS,
  NOTIFICATION_DELIVERY_STATUSES,
  type NotificationDelivery,
  type NotificationDeliveryStatus,
} from '@/shared/api/notifications';

/**
 * Apart from the page so neither file exports anything but a component (`auditOptions.ts`): the
 * `validateSearch` of `/admin/notification-deliveries` and the words for why a delivery did not go out.
 */

/** Attempts a failing email gets before it stays `failed` (`SendNotificationJob`: the first send and four retries; a recipient without an address or a template that does not parse stops at the first). */
export const MAX_DELIVERY_ATTEMPTS = 5;

/** The shape `notification_type` is validated against on the server (`NotificationTypeFormat`). */
const TYPE_FORMAT = /^[a-z][a-z0-9_]{1,63}$/u;

/** Every filter lives in the URL, so a link into a filtered list is a plain navigation. */
export interface NotificationDeliveriesSearch {
  status?: NotificationDeliveryStatus;
  channel?: (typeof NOTIFICATION_DELIVERY_CHANNELS)[number];
  notificationType?: string;
  from?: string;
  to?: string;
}

/** A value the collection would refuse is dropped instead of thrown on, like every other search schema. */
export function notificationDeliveriesSearchSchema(
  search: Record<string, unknown>,
): NotificationDeliveriesSearch {
  const result: NotificationDeliveriesSearch = {};

  const status = search['status'];
  if (
    typeof status === 'string' &&
    NOTIFICATION_DELIVERY_STATUSES.includes(status as NotificationDeliveryStatus)
  ) {
    result.status = status as NotificationDeliveryStatus;
  }

  const channel = search['channel'];
  if (
    typeof channel === 'string' &&
    NOTIFICATION_DELIVERY_CHANNELS.includes(
      channel as (typeof NOTIFICATION_DELIVERY_CHANNELS)[number],
    )
  ) {
    result.channel = channel as (typeof NOTIFICATION_DELIVERY_CHANNELS)[number];
  }

  const notificationType = search['notificationType'];
  if (typeof notificationType === 'string' && TYPE_FORMAT.test(notificationType)) {
    result.notificationType = notificationType;
  }

  // Both ends travel together: a lone `from` or `to` is not a range (`auditLogSearchSchema`).
  const from = parseIsoDate(search['from']);
  const to = parseIsoDate(search['to']);
  if (from !== undefined && to !== undefined) {
    result.from = from;
    result.to = to;
  }

  return result;
}

/**
 * The reasons the server writes into `last_error` as a code, each with its words in
 * `admin:notificationDeliveries.reason.<code>`: `SendNotificationJob` (`template_missing`,
 * `recipient_without_email`, `template_syntax`, `template_render_limit`), `OutboxDispatcher`
 * (`muted`, `channel_not_supported`, and the two template codes on an in-app notice that went out
 * with the fixed title, AM-356) and `StalledEmailDeliveries` (`not_processed`, AM-357: an email
 * that sat in the queue with no movement after the system put it back twice).
 */
const CODED_REASONS = [
  'template_missing',
  'muted',
  'channel_not_supported',
  'recipient_without_email',
  'template_syntax',
  'template_render_limit',
  'not_processed',
] as const;

export type CodedReason = (typeof CODED_REASONS)[number];

/**
 * AM-356: the codes a `sent` delivery may still carry. An in-app notice whose template could not
 * render reached the bell with the event's fixed title, and keeps why; its note reads
 * `admin:notificationDeliveries.reason.sent_<code>`, not the words of a mail that did not go out.
 */
const SENT_WITH_FALLBACK = ['template_syntax', 'template_render_limit'] as const;

type SentReasonKey = `sent_${(typeof SENT_WITH_FALLBACK)[number]}`;

export type DeliveryReasonKey =
  CodedReason | SentReasonKey | 'queued' | 'retrying' | 'failed' | 'other';

/**
 * What `EmailNotificationChannel` wrote into `last_error` for a recipient without an address before
 * the job kept the code instead (`SendNotificationJob.FailureReason`): deliveries that failed then
 * still carry it, and it is the same reason, so it reads as one rather than as a mail server that
 * refused the message.
 */
const LEGACY_RECIPIENT_WITHOUT_EMAIL = /^The recipient has no e-mail address/u;

/** The code `last_error` stands for, or `null` when it is a transport's message, an unknown code or nothing. */
export function codedReason(lastError: string | null): CodedReason | null {
  if (lastError === null) {
    return null;
  }

  if ((CODED_REASONS as readonly string[]).includes(lastError)) {
    return lastError as CodedReason;
  }

  return LEGACY_RECIPIENT_WITHOUT_EMAIL.test(lastError) ? 'recipient_without_email' : null;
}

/** Whether `last_error` is one of the codes above, and so carries nothing more to read. */
export function isCodedReason(lastError: string | null): boolean {
  return codedReason(lastError) !== null;
}

/** Whether the template code of a `sent` delivery is one it keeps on purpose (AM-356). */
function isSentWithFallback(code: CodedReason): code is (typeof SENT_WITH_FALLBACK)[number] {
  return (SENT_WITH_FALLBACK as readonly string[]).includes(code);
}

/**
 * Why a delivery is not `sent`, as a key of `admin:notificationDeliveries.reason` (and
 * `reasonHelp`), or `null` for one that went out as it should. A code wins; without one, a pending
 * delivery with attempts is being retried, a failed one ran out of them, and a suppressed one with
 * a code the console does not know still says which. A `sent` delivery has a note only when it went
 * out with the fixed title because its template could not render (AM-356).
 */
export function deliveryReason(
  delivery: Pick<NotificationDelivery, 'status' | 'attempts' | 'lastError'>,
): DeliveryReasonKey | null {
  const coded = codedReason(delivery.lastError);

  if (delivery.status === 'sent') {
    return coded !== null && isSentWithFallback(coded) ? `sent_${coded}` : null;
  }

  if (coded !== null) {
    return coded;
  }

  switch (delivery.status) {
    case 'pending':
      return delivery.attempts > 0 ? 'retrying' : 'queued';
    case 'failed':
      return 'failed';
    default:
      return 'other';
  }
}
